/**
 * SCHEDULE tab of the cohort detail view (F3-A): weekly slot editor with
 * conflict pre-flight (room/teacher), bulk session generation button, and
 * session list. Local-first writing (the write-path validates the conflict
 * again, the HARD way); generation goes through REST.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useNavigate } from "@tanstack/solid-router";
import { ListChecks, Plus, Trash2, Video } from "@fleet/icons";
import {
  createSlotMutation,
  deleteSlotMutation,
  handleApiResponse,
  joinWindowState,
  localDateKey,
  restoreTrashedMutation,
} from "@space/shared";
import { useUndo } from "@/lib/undo";
import { api } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { waitForUploadQueueDrain } from "@/lib/powersync/wait-upload";
import { useCohortSessions, useCohortSlots, useRoomsByOrg } from "../hooks/use-schedule-data";
// F3-G: live classes — badge/enter/recording + provider settings.
import { EnVivoBadge } from "@/features/live/components/en-vivo-badge";
import { MeetingSettings } from "@/features/live/components/meeting-settings";
import { RecordingAttach } from "@/features/live/components/recording-attach";
import { RecordingPlayer } from "@/features/live/components/recording-player";
import { SessionMeetingButton } from "@/features/live/components/session-meeting-button";
import { liveApi } from "@/features/live/lib/live-api";
import { useNow } from "@/features/live/hooks/use-now";
import { useCohort } from "@/features/live/hooks/use-live-data";
// M5: class plan editor (flipped classroom), gated by feature.
import { SessionPlanEditor } from "./session-plan-editor";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

const WEEKDAYS: { value: number; label: MessageDescriptor }[] = [
  { value: 1, label: msg`Monday` },
  { value: 2, label: msg`Tuesday` },
  { value: 3, label: msg`Wednesday` },
  { value: 4, label: msg`Thursday` },
  { value: 5, label: msg`Friday` },
  { value: 6, label: msg`Saturday` },
  { value: 0, label: msg`Sunday` },
];

export const CohortScheduleTab: Component<{
  cohortId: string;
  organizationId: string;
  campusId: string | null;
}> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const powerSync = usePowerSyncContext();
  const { registerUndo } = useUndo();
  const navigate = useNavigate();
  const now = useNow();
  const slots = useCohortSlots(() => props.cohortId);
  const sessions = useCohortSessions(() => props.cohortId);
  const rooms = useRoomsByOrg(() => props.organizationId);
  const cohort = useCohort(() => props.cohortId);

  // F3-G: cohort's default live provider (cohort.config.media_settings).
  const cohortMediaSettings = createMemo<Record<string, unknown>>(() => {
    const config = cohort.data?.[0]?.config;
    let parsed: Record<string, unknown> = {};
    if (typeof config === "string") {
      try {
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        parsed = JSON.parse(config) as Record<string, unknown>;
      } catch {
        parsed = {};
      }
    } else if (config && typeof config === "object") {
      parsed = config as Record<string, unknown>;
    }
    const ms = (parsed as { media_settings?: Record<string, unknown> }).media_settings;
    return ms ?? {};
  });
  const initialProvider = () =>
    (cohortMediaSettings() as { meetingProvider?: string }).meetingProvider ?? null;
  const initialConfig = (): Record<string, unknown> =>
    (cohortMediaSettings() as { meetingProviderConfig?: Record<string, unknown> })
      .meetingProviderConfig ?? {};

  const enterLive = (sessionId: string) => {
    void navigate({
      to: "/live/$sessionId",
      params: { sessionId },
    });
  };

  // M5: class plan (flipped classroom) — feature + editor open per session.
  const flipped = useFeatureEnabled("academy.flipped_sessions");
  const courseId = () => cohort.data?.[0]?.course_id ?? undefined;
  const [planOpen, setPlanOpen] = createSignal<string | null>(null);

  const [weekday, setWeekday] = createSignal(1);
  const [start, setStart] = createSignal("18:00");
  const [end, setEnd] = createSignal("20:00");
  const [roomId, setRoomId] = createSignal("");
  const [conflict, setConflict] = createSignal<string | null>(null);

  // LOCAL dates: with the UTC date, in UTC-4 starting at 20:00 the range
  // started "tomorrow" and TODAY's session was never generated (COV).
  const [from, setFrom] = createSignal(localDateKey());
  const [to, setTo] = createSignal(localDateKey(new Date(Date.now() + 60 * 86_400_000)));

  const roomName = (id: string | null) =>
    id ? ((rooms.data ?? []).find((r) => r.id === id)?.name ?? id) : "—";

  const weekdayLabel = (w: number | null) =>
    t(WEEKDAYS.find((d) => d.value === w)?.label ?? msg`Day`);

  const addSlot = async () => {
    const db = drizzle();
    if (!db) {
      return;
    }
    setConflict(null);
    // Conflict pre-flight (double-booked room / teacher overlap).
    try {
      const res = await handleApiResponse(
        api.POST("/api/schedule/validate", {
          body: {
            cohort_id: props.cohortId,
            kind: "slot",
            weekday: weekday(),
            start_time: start(),
            end_time: end(),
            room_id: roomId() ? roomId() : null,
          },
        }),
      );
      if (res && !res.ok) {
        setConflict(t(msg`Schedule conflict: room or teacher already booked.`));
        return;
      }
    } catch {
      // If the pre-flight fails, the write-path still validates the hard way.
    }
    await createSlotMutation(
      db,
      { cohortId: props.cohortId, organizationId: props.organizationId, campusId: props.campusId },
      {
        weekday: weekday(),
        startTime: start(),
        endTime: end(),
        roomId: roomId() ? roomId() : null,
      },
    );
  };

  const removeSlot = (id: string) => {
    const db = drizzle();
    if (!db) {
      return;
    }
    // Captured OUTSIDE the undo closure (solid/reactivity): reading
    // `props.organizationId` inside the function passed to `registerUndo` is
    // not in a tracked scope.
    // oxlint-disable-next-line prefer-destructuring
    const organizationId = props.organizationId;
    // F4-A: soft-delete + "Undo" toast (also restorable from /trash).
    // `registerUndo` already covers the undo's own failure; what's missing is
    // the initial delete: `patchRowVerified` can throw and this is fire-and-forget.
    void (async () => {
      try {
        await deleteSlotMutation(db, id, organizationId);
      } catch (err) {
        console.error("Error deleting schedule slot:", err);
        showToast(t(msg`Could not delete the schedule slot`), "error");
        return;
      }
      registerUndo(t(msg`Schedule slot moved to trash`), async () => {
        const d = drizzle();
        if (d) {
          await restoreTrashedMutation(d, "schedule_slot", id, organizationId);
        }
      });
    })();
  };

  const generate = async () => {
    try {
      // local-first→REST seam: newly-added slots travel through PowerSync's
      // upload queue; the generator READS schedule_slot on the server. We
      // drain the queue so as not to generate against an empty schedule.
      const ps = powerSync.db();
      if (ps) {
        await waitForUploadQueueDrain(ps);
      }
      const res = await handleApiResponse(
        api.POST("/api/cohorts/{id}/generate-sessions", {
          params: { path: { id: props.cohortId } },
          body: { from: from(), to: to() },
        }),
      );
      const created = res?.created ?? 0;
      const skipped = res?.skipped ?? 0;
      showToast(t(msg`Generated ${created} sessions (${skipped} already existed)`), "success");
    } catch {
      showToast(t(msg`Could not generate sessions`), "error");
    }
  };

  const sortedSessions = createMemo(() =>
    (sessions.data ?? []).toSorted((a, b) => (a.date ?? "").localeCompare(b.date ?? "")),
  );

  return (
    <div class="space-y-5" data-testid="cohort-schedule">
      {/* Slot editor */}
      <section class="space-y-3 rounded-2xl border border-border p-4">
        <h3 class="text-sm font-bold">{t(msg`Weekly slots`)}</h3>
        <div class="grid grid-cols-2 gap-2 md:grid-cols-5">
          <select
            class={field}
            data-testid="slot-weekday"
            value={weekday()}
            onChange={(e) => setWeekday(Number(e.currentTarget.value))}
          >
            <For each={WEEKDAYS}>{(d) => <option value={d.value}>{t(d.label)}</option>}</For>
          </select>
          <input
            type="time"
            class={field}
            data-testid="slot-start"
            value={start()}
            onInput={(e) => setStart(e.currentTarget.value)}
          />
          <input
            type="time"
            class={field}
            data-testid="slot-end"
            value={end()}
            onInput={(e) => setEnd(e.currentTarget.value)}
          />
          <select
            class={field}
            data-testid="slot-room"
            value={roomId()}
            onChange={(e) => setRoomId(e.currentTarget.value)}
          >
            <option value="">{t(msg`No room`)}</option>
            <For each={rooms.data ?? []}>{(r) => <option value={r.id}>{r.name}</option>}</For>
          </select>
          <Button data-testid="slot-add" onClick={() => void addSlot()} class="gap-1">
            <Plus class="h-4 w-4" /> {t(msg`Add`)}
          </Button>
        </div>
        <Show when={conflict()}>
          <p class="text-xs font-semibold text-danger" data-testid="slot-conflict">
            {conflict()}
          </p>
        </Show>
        <Show
          when={(slots.data ?? []).length > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No slots yet.`)}</p>}
        >
          <ul class="space-y-1">
            <For each={slots.data ?? []}>
              {(s) => (
                <li
                  class="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm"
                  data-testid="slot-row"
                >
                  <span>
                    <strong>{weekdayLabel(s.weekday)}</strong> {(s.start_time ?? "").slice(0, 5)}–
                    {(s.end_time ?? "").slice(0, 5)} · {roomName(s.room_id)}
                  </span>
                  <button
                    type="button"
                    data-testid="slot-delete"
                    onClick={() => removeSlot(s.id)}
                    class="text-muted-foreground hover:text-danger"
                    aria-label={t(msg`Delete slot`)}
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </section>

      {/* Bulk generation */}
      <section class="space-y-3 rounded-2xl border border-border p-4">
        <h3 class="text-sm font-bold">{t(msg`Generate sessions`)}</h3>
        <div class="flex flex-wrap items-end gap-2">
          <label class="space-y-1 text-xs text-muted-foreground">
            {t(msg`From`)}
            <input
              type="date"
              class={field}
              data-testid="generate-from"
              value={from()}
              onInput={(e) => setFrom(e.currentTarget.value)}
            />
          </label>
          <label class="space-y-1 text-xs text-muted-foreground">
            {t(msg`To`)}
            <input
              type="date"
              class={field}
              data-testid="generate-to"
              value={to()}
              onInput={(e) => setTo(e.currentTarget.value)}
            />
          </label>
          <Button variant="outline" data-testid="generate-sessions" onClick={() => void generate()}>
            {t(msg`Generate`)}
          </Button>
        </div>
      </section>

      {/* F3-G: cohort's default live class provider */}
      <MeetingSettings
        title={t(msg`Live class provider`)}
        description={t(
          msg`Default provider for this cohort's live sessions. Each session can override it.`,
        )}
        initialProvider={initialProvider()}
        initialConfig={initialConfig()}
        onSave={(provider, config) => liveApi.setCohortDefaults(props.cohortId, provider, config)}
      />

      {/* Session list */}
      <section class="space-y-2">
        <h3 class="text-sm font-bold">{t(msg`Sessions`)}</h3>
        <Show
          when={sortedSessions().length > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No sessions yet.`)}</p>}
        >
          <ul class="space-y-1">
            <For each={sortedSessions()}>
              {(s) => {
                const times = () => ({
                  date: s.date,
                  startTime: s.start_time,
                  endTime: s.end_time,
                });
                const isLive = () => joinWindowState(now(), times()).isLiveNow;
                return (
                  <li
                    class="space-y-2 rounded-lg border border-border px-3 py-2 text-sm"
                    data-testid="session-row"
                    data-session-status={s.status ?? ""}
                  >
                    <div class="flex flex-wrap items-center justify-between gap-2">
                      <span class="flex items-center gap-2">
                        {s.date} · {(s.start_time ?? "").slice(0, 5)}–
                        {(s.end_time ?? "").slice(0, 5)} · {roomName(s.room_id)}
                        <EnVivoBadge times={times()} />
                      </span>
                      <span class="flex flex-wrap items-center gap-2">
                        {/* M5: open/close the class plan (flipped classroom) */}
                        <Show when={flipped().enabled}>
                          <button
                            type="button"
                            class="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-semibold hover:bg-muted"
                            data-testid="session-plan-toggle"
                            aria-expanded={planOpen() === s.id}
                            onClick={() => setPlanOpen(planOpen() === s.id ? null : s.id)}
                          >
                            <ListChecks class="h-3.5 w-3.5" /> {t(msg`Plan`)}
                          </button>
                        </Show>
                        {/* Enter (teacher) when live */}
                        <Show when={isLive()}>
                          <button
                            type="button"
                            class="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground"
                            data-testid="session-enter-live"
                            onClick={() => enterLive(s.id)}
                          >
                            <Video class="h-3.5 w-3.5" /> {t(msg`Enter`)}
                          </button>
                        </Show>
                        {/* Recording: view if it exists, or upload */}
                        <Show
                          when={s.recording_video_id}
                          fallback={
                            <RecordingAttach sessionId={s.id} title={s.topic ?? undefined} />
                          }
                        >
                          <RecordingPlayer
                            videoId={s.recording_video_id!}
                            title={s.topic ?? undefined}
                          />
                        </Show>
                        <SessionMeetingButton
                          sessionId={s.id}
                          initialProvider={s.meeting_provider}
                        />
                        <span class="rounded-full bg-accent/12 px-2 py-0.5 text-xs font-bold text-accent">
                          {s.status}
                        </span>
                      </span>
                    </div>
                    {/* M5: this session's class plan editor */}
                    <Show when={flipped().enabled && planOpen() === s.id && courseId()}>
                      <SessionPlanEditor
                        sessionId={s.id}
                        cohortId={props.cohortId}
                        organizationId={props.organizationId}
                        campusId={props.campusId}
                        courseId={courseId()!}
                      />
                    </Show>
                  </li>
                );
              }}
            </For>
          </ul>
        </Show>
      </section>
    </div>
  );
};
